import React, { useLayoutEffect, useRef, useState } from "react";
import PropertyListing from "@/components/Onboarding/Dashboard/v2/PropertyListing";
import AgentInventoryListing from "@/components/AgentDashboard/v2/AgentInventoryListing";
import MatchDemandPanel from "@/components/Onboarding/Dashboard/MatchDemandPanel";
import { BUY } from "@/constants/common";
import { useAppConfig } from "@/appConfig";
import { pageURLs } from "@/constants";
import { feedRow, placeOf, propertyRow, sampleAgentOf, sampleDemandList, TOUR_SAMPLE_SLUG } from "./seeds";
import { compactPrice } from "@/helpers/compactPrice";

const noop = () => {};

const Scaled = ({ width, scale, crop, className = "", children }) => {
  const innerRef = useRef(null);
  const outerRef = useRef(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    outerRef.current?.setAttribute("inert", "");
    const el = innerRef.current;
    if (!el) return undefined;
    const update = () => setHeight(el.offsetHeight * scale);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scale]);
  return (
    <div
      ref={outerRef}
      className={`tour-real ${className}`}
      aria-hidden="true"
      style={{ width: (crop || width) * scale, height }}
    >
      <div className="tour-real__inner" ref={innerRef} style={{ width, transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
};

export const FeedCardPreview = ({ samples, scale = 0.5, width = 820, vertical = false, row: given }) => {
  const { tenant } = useAppConfig() || {};
  const row = given || feedRow(samples, placeOf(samples, tenant), sampleAgentOf(tenant));
  return (
    <Scaled width={width} scale={scale} className={vertical ? "tour-real--vertical" : ""}>
      <div className="property-listing-outer">
        <PropertyListing
          list={row}
          handleFavoriteOrUnfavoriteWantAd={noop}
          results={[]}
          clientSearchText=""
          onChangeText={noop}
          onAddClient={noop}
          handleWantAdItemClick={noop}
        />
      </div>
    </Scaled>
  );
};

export const InventoryRowPreview = ({ samples, scale = 0.5, width = 1100, crop = 640 }) => {
  const { tenant } = useAppConfig() || {};
  const row = propertyRow(samples, placeOf(samples, tenant));
  return (
    <Scaled width={width} scale={scale} crop={crop} className="tour-real--no-date">
      <div className="horizontal-tab-wrap">
        <AgentInventoryListing
          showNoResult
          handleSort={noop}
          itemList={[row]}
          onItemEdit={noop}
          onItemDelete={noop}
          sort={{ order: "desc", orderBy: "date" }}
          emptyItemHtml={null}
          loading={false}
          hasMore={false}
          loadingMore={false}
          onShowMore={noop}
        />
      </div>
    </Scaled>
  );
};

export const WantListingPagePreview = ({ scale = 0.42, width = 1180, height = 2300, frame = 560 }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  return (
    <div
      ref={ref}
      className="tour-real tour-real--page"
      aria-hidden="true"
      style={{ width: width * scale, height: frame, "--travel": `${-(height * scale - frame)}px` }}
    >
      <iframe
        title="Sample Buyer Want-Listing"
        src={`${pageURLs.wantAdDetails}/${TOUR_SAMPLE_SLUG}?tourPreview=1`}
        tabIndex={-1}
        style={{ width, height, transform: `scale(${scale})` }}
      />
    </div>
  );
};

const MATCHING_BUYERS = [
  { title: (city) => `Seeking a single-story 3-bed in ${city}`, agent: ["Taylor", "Brooks", "Brooks Realty"], at: 0.35 },
  { title: (city) => `Need a 3-bed with a garage near ${city}`, agent: ["Morgan", "Reyes", "Reyes Group"], at: 0.75 },
];

const matchingRows = (samples, tenant) => {
  const place = placeOf(samples, tenant);
  const { demandBudgetMin = 300000, demandBudgetMax = 360000 } = samples.property || {};
  return MATCHING_BUYERS.map((buyer, index) => {
    const mid = demandBudgetMin + (demandBudgetMax - demandBudgetMin) * buyer.at;
    const [firstName, lastName, company] = buyer.agent;
    return {
      ...feedRow(samples, place, { ...sampleAgentOf(tenant), firstName, lastName, company, userId: -(index + 2), avatar: "" }),
      Title_Text: buyer.title(place.city || "town"),
      Budget_mn: Math.round((mid - 25000) / 5000) * 5000,
      Budget_mx: Math.round((mid + 25000) / 5000) * 5000,
    };
  });
};

export const DemandStackPreview = ({ samples }) => {
  const { tenant } = useAppConfig() || {};
  const rows = matchingRows(samples, tenant);
  return (
    <div className="tour-demand-stack">
      {rows.map((row, index) => (
        <div key={row.Title_Text} className={`tour-demand-stack__card tour-demand-stack__card--${index + 1}`}>
          <FeedCardPreview samples={samples} row={row} scale={0.4} width={760} />
        </div>
      ))}
      <div className="tour-demand-stack__panel">
        <DemandPanelPreview samples={samples} />
      </div>
    </div>
  );
};

export const DemandPanelPreview = ({ samples }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  const { tenant } = useAppConfig() || {};
  const list = sampleDemandList(samples);
  const property = { ...propertyRow(samples, placeOf(samples, tenant)), Inv_Id: -1, reportMatchCount: list.length };
  return (
    <div ref={ref} className="tour-real tour-real--panel" aria-hidden="true">
      <h6>Demand insights</h6>
      <p className="feed-section-note">Want-Listings whose criteria match this property.</p>
      <MatchDemandPanel
        selectedProperty={property}
        adType={BUY}
        totalCountMatch={list.length}
        adListGitchaGenie={list}
        isAgent
        loading={false}
      />
    </div>
  );
};

const initialsOf = (person) => `${person.firstName?.[0] || ""}${person.lastName?.[0] || ""}` || "You";

export const ConnectPreview = ({ samples }) => {
  const { tenant } = useAppConfig() || {};
  const agent = sampleAgentOf(tenant);
  const place = placeOf(samples, tenant);
  const property = propertyRow(samples, place);
  const sample = samples.wantListing;
  const [first, second] = (sample.niceToHaves || []).map((item) => item?.Text?.toLowerCase()).filter(Boolean);
  const features = [first, second].filter(Boolean).join(" and ");
  const reducedFrom = Math.round((property.AskingPrice_Sell + 10000) / 1000) * 1000;
  const price = compactPrice(property.AskingPrice_Sell);
  const wasPrice = compactPrice(reducedFrom);
  return (
    <div className="tour-connect" aria-hidden="true">
      <div className="tour-connect__head">
        <span className="tour-connect__avatar">{initialsOf(agent)}</span>
        <div>
          <strong>
            {agent.firstName} {agent.lastName}
          </strong>
          <span>{sample.title}</span>
        </div>
      </div>
      <div className="tour-connect__thread">
        <div className="tour-connect__intro">
          <img src={property.photo} alt="" />
          <div>
            <span className="tour-connect__label">Introduction</span>
            <strong>
              {price} {wasPrice !== price ? <s>{wasPrice}</s> : null}
            </strong>
            <span>{property.Location}</span>
          </div>
        </div>
        <p className="tour-connect__bubble is-mine">
          {features
            ? `Just reduced by $10K. It has the ${features} your Buyer Want-Listing calls out, and the seller is open to a rate buydown.`
            : "Just reduced by $10K, and the seller is open to a rate buydown."}
        </p>
        <div className="tour-connect__reply">
          <span className="tour-connect__avatar is-small">{initialsOf(agent)}</span>
          <p className="tour-connect__bubble">That checks both boxes. Can we see it Thursday afternoon?</p>
        </div>
      </div>
    </div>
  );
};
